import get from 'lodash/get'

import { predefinedSchemas } from './PresetComponentsList'

export function getJsonPath(parentPath = '', fieldIndex = 0, fieldName = '', isNonArrayObject = false) {
  const index = !isNonArrayObject && fieldIndex !== -1 ? `[${fieldIndex}]` : ''
  return [parentPath, index, fieldName].filter(Boolean).join('.')
}

export function getJsonValue(jsonPath = '', jsonData = []) {
  return get(jsonData, jsonPath)
}

export function getListSchema(components = []) {
  if (!components[0]) return
  const schema = { component: { type: 'preset', name: components[0] } }
  for (const key of components) {
    const fields = predefinedSchemas[key]
    Object.assign(schema, fields)
  }
  return schema
}

/**
 * Checks if a field should be visible based on the `visibleIf` condition.
 * The condition is a string representing the path to a sibling field of the JSON
 * object and the expected value.
 *
 * @param {string} condition - The visibility condition string.
 * The format is expected to be like "path.to.field=targetValue".
 * It can also include logical operators like "||" (OR) and "&" (AND).
 * @param {string} jsonPath - The path to the field being checked.
 * @param {Object} jsonData - The current JSON data of the StructuredJson.
 * @returns {boolean} - Returns true if the field should be visible, false otherwise.
 */
export function checkFieldVisibility(condition, jsonPath, jsonData) {
  if (!condition || !jsonData) return true

  const matchCond = (cond) => {
    let [targetProp, targetValue] = cond.split('=')
    let negate = false
    if (targetProp.startsWith('!')) {
      targetProp = targetProp.slice(1)
      negate = true
    }
    const targetPath = jsonPath.split('.')
    targetPath.pop()
    targetPath.push(targetProp)
    const value = get(jsonData, targetPath.join('.'))
    return negate
      ? value.toString() !== targetValue
      : value.toString() === targetValue || (targetValue === 'notNull' && !!value)
  }

  return condition.split('||').some((val) => {
    if (val.includes('&')) return val.split('&').every(matchCond)
    else return val.split('|').some(matchCond)
  })
}

/**
 * Returns the first item of the array if the schema expects an object;
 * otherwise returns the data as-is. Falls back to {} if the array is empty.
 *
 * @param {*} data - The input data (array or object).
 * @param {boolean} isNonArrayObject - Whether the schema expects an object.
 * @returns {*} - Normalized object or original data.
 */
export function normalizeToObject(data, isNonArrayObject) {
  return isNonArrayObject && Array.isArray(data) ? data[0] ?? {} : data
}

export function getDefaultJsonValue(schema) {
  const value = {}
  for (const [key, val] of Object.entries(schema)) {
    switch (val?.type) {
      case 'string':
      case 'enum':
        value[key] = ''
        break
      case 'number':
        value[key] = 0
        break
      case 'boolean':
        value[key] = false
        break
      case 'array':
        value[key] = [getDefaultJsonValue(val.schema || {})]
        break
      case 'list':
        const listSchema = getListSchema(val.components)
        value[key] = getDefaultJsonValue(listSchema) || {}
        break
      case 'preset':
        value[key] = val.name
        break
      case 'richtext':
        value[key] = {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              attrs: {
                textAlign: 'left',
              },
            },
          ],
        }
        break
      default:
        // In case the type is unknown no value will be defined
        break
    }
  }
  return value
}
